import Box from '@mui/material/Box';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { useContext, useEffect, useState } from 'react';
import { Link, NavLink, useLocation, withRouter } from 'react-router-dom';
import Select from 'react-select';
import docImg from '../../../assets/icons/document.png';
import BadgeStatus from '../../../common/BadgeStatus';
import Button from '../../../components/Button';
import Searchbar from '../../../components/Searchbar';
import ToastUtil from '../../../components/ToastNotification/ToastUtil';
import { config } from '../../../config';
import { GET, newFetch, POST } from '../../../helpers/fetch';
import { getSelectedSiteIds } from '../../../helpers/general';
import AppContext from '../../../store/App/contexts';

// Suppress ResizeObserver warnings
const originalError = console.error;
console.error = (...args) => {
  if (
    args[0] &&
    ((typeof args[0] === 'string' && args[0].includes('ResizeObserver')) ||
      args[0].message?.includes('ResizeObserver'))
  ) {
    return;
  }
  originalError.apply(console, args);
};

const { SERVER_URL } = config;

const Leaves = ({ history }) => {
  const { selectedClientID, selectedSiteID } = useContext(AppContext);
  const clientID = parseInt(selectedClientID, 10);
  const siteID = parseInt(selectedSiteID, 10);
  const siteIDs = getSelectedSiteIds(clientID, siteID);
  const siteIDsKey = siteIDs.join(',');
  const userID = JSON.parse(localStorage.getItem('user') || '{}')?.id;
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [leaves, setLeaves] = useState([]);
  const [claimTypes, setClaimTypes] = useState([]);
  const [adminClients, setAdminClients] = useState();
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [sortModel, setSortModel] = useState([
    { field: 'updatedAt', sort: 'desc' },
  ]);
  const [totalRows, setTotalRows] = useState(0);
  const [data, setData] = useState([]);
  const type = localStorage.getItem('type');
  const [clients, setClients] = useState([]);
  const [searchText, setSearchText] = useState('');

  // State for multi-select filters
  const [associatesFilter, setAssociatesFilter] = useState([]);
  const [leaveTypeFilter, setLeaveTypeFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [associateOptions, setAssociateOptions] = useState([]);
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const availableLeaveTypeOptions = Array.from(
    new Map(
      [
        ...leaveTypeOptions,
        ...leaves
          .filter((leave) => leave.leave_type && leave.leave_type_name)
          .map((leave) => ({
            value: leave.leave_type,
            label:
              leave.leave_type_option_name || leave.leave_type_name,
          })),
      ].map((option) => [Number(option.value), option])
    ).values()
  );
  const availableStatusOptions = Array.from(
    new Map(
      [
        ...statusOptions,
        ...leaves
          .filter((leave) => leave.status)
          .map((leave) => ({
            value: leave.status,
            label: leave.status,
          })),
      ].map((option) => [option.value, option])
    ).values()
  );

  // Year and month filters
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(0);
  const [filterYear, setFilterYear] = useState(currentYear);
  const [filterMonth, setFilterMonth] = useState(0);

  const columns = [
    {
      field: 'leave_id',
      headerName: 'Request ID',
      flex: 0.6,
      sortable: true,
      renderCell: (params) => {
        return (
          <Link
            className="text-primary"
            to={{
              pathname: `/leave/approval/edit/${params.row.leave_id}/approver/${params.row.approver_level}`,
              state: { from: location.pathname },
            }}
            style={{ fontWeight: 700, fontSize: 14 }}
          >
            {params.row.leave_id}
          </Link>
        );
      },
    },
    {
      field: 'pb_cand_id',
      headerName: 'PB Candidate ID',
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        return (
          <span className="text-black-default">
            {params.row.pb_cand_id || ''}
          </span>
        );
      },
    },
    {
      field: 'pb_jo_id',
      headerName: 'PB JO ID',
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        return (
          <span className="text-black-default">
            {params.row.pb_jo_id || ''}
          </span>
        );
      },
    },
    {
      field: 'name',
      headerName: 'Associate',
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const fullName = `${params.row.name || ''} ${
          params.row.last_name || ''
        }`.trim();
        return <span className="text-black-default">{fullName || ''}</span>;
      },
    },
    {
      field: 'client',
      headerName: 'Client',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <span className="text-black-default">{params.row.client || ''}</span>
      ),
    },
    {
      field: 'sites',
      headerName: 'Site',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <span className="text-black-default">
          {params.row.sites || params.row.site_name || ''}
        </span>
      ),
    },
    {
      field: 'leave_type_name',
      headerName: 'Leave Type',
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <span className="text-black-default">{params.value || ''}</span>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1.5,
      sortable: true,
      renderCell: (params) => {
        return (
          <>
            <BadgeStatus status={params.value || ''} />
          </>
        );
      },
    },
    {
      field: 'leave_from',
      headerName: 'Leave From',
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <span className="text-black-default">{params.value || ''}</span>
      ),
    },
    {
      field: 'leave_to',
      headerName: 'Leave To',
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <span className="text-black-default">{params.value || ''}</span>
      ),
    },
    {
      field: 'no_of_days',
      headerName: 'Total Days',
      flex: 0.7,
      sortable: true,
      renderCell: (params) => (
        <span className="text-black-default">{params.value || ''}</span>
      ),
    },
    {
      field: 'leave_coverage',
      headerName: 'Day Type',
      flex: 0.7,
      sortable: true,
      renderCell: (params) => {
        const coverageMap = {
          0: 'Full Day',
          1: 'Half Day (AM)',
          2: 'Half Day (PM)',
        };

        const value = coverageMap[params.value] || '';

        return <span className="text-black-default">{value}</span>;
      },
    },
    {
      field: 'attachments',
      headerName: 'Attachments',
      flex: 0.8,
      sortable: false,
      renderCell: (params) => {
        const attachments = params.row.documents || [];
        if (attachments.length === 0)
          return <span className="text-black-default"></span>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {attachments.map((file, idx) => (
              <div
                key={idx}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <a
                  href={`${SERVER_URL}/storage/leaves/${file.new_file}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-flex align-items-center"
                >
                  <img
                    src={docImg}
                    alt="Document Icon"
                    style={{ width: '32px', height: '32px' }}
                  />
                </a>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Date Modified',
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <span className="text-black-default">{params.value || ''}</span>
      ),
    },
  ];

  const fetchAdminClients = async () => {
    try {
      const fetchUrl = `/admin/admins-client/${userID}`;
      const response = await newFetch(GET, fetchUrl);

      if (response.success) {
        setAdminClients(response.data);
      }
    } catch (err) {
      console.error('Error fetching admin clients:', err);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const fetchUrl = '/leaves/approver/filter-options';
      const response = await newFetch(POST, fetchUrl, {
        client_id: clientID,
        site_id: siteID,
        site_ids: siteIDs,
        sites: siteIDs,
        type,
        year: filterYear,
        month: filterMonth,
        search: searchText,
      });

      if (response.success) {
        setAssociateOptions(response.data.associates || []);
        setLeaveTypeOptions(response.data.leaveTypes || []);
        setStatusOptions(response.data.statuses || []);
      }
    } catch (err) {
      console.log('Error fetching filter options:', err);
    }
  };

  const fetchLeaves = async (pagination, field, sort, search) => {
    try {
      setLoading(true);
      const fetchUrl = '/leaves/approver/view';
      const response = await newFetch(POST, fetchUrl, {
        field: field || 'updatedAt',
        sort: sort || 'desc',
        page: pagination.page,
        pageSize: pagination.pageSize,
        client_id: clientID,
        site_id: siteID,
        site_ids: siteIDs,
        sites: siteIDs,
        type,
        search: search || '',
        associates: associatesFilter.map((opt) => opt.value),
        leave_types: leaveTypeFilter.map((opt) => opt.value),
        statuses: statusFilter.map((opt) => opt.value),
        year: filterYear,
        month: filterMonth,
      });

      if (response.success) {
        setLeaves(response.data.list);
        setTotalRows(response.data.total_record);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const handleSortModelChange = (newSortModel) => setSortModel(newSortModel);

  const fetchTypes = async () => {
    try {
      let response = await newFetch(GET, `/claimtypes`);
      if (response.success) setClaimTypes(response.data);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchClients = async () => {
    let response = await newFetch(GET, `/client/${clientID}`);
    if (response.success) setClients(response.data);
  };

  const searchHandler = (e) => {
    setSearchText(e.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Enhanced export function to handle large datasets
  const handleExport = async () => {
    setLoading(true);
    try {
      // Fetch all data for export
      const exportData = await fetchLeavesForExport();

      if (exportData && exportData.length > 0) {
        exportToCSV(
          exportData,
          columns,
          `leaves_${filterYear || 'all'}_${filterMonth || 'all'}.csv`,
        );
      } else {
        ToastUtil.error('No data found for export');
      }
    } catch (error) {
      console.error('Export error:', error);
      ToastUtil.error('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  // Separate function to fetch all data for export
  const fetchLeavesForExport = async () => {
    try {
      const fetchUrl = '/leaves/approver/view';
      const response = await newFetch(POST, fetchUrl, {
        field: sortModel[0]?.field || 'updatedAt',
        sort: sortModel[0]?.sort || 'desc',
        page: 0,
        pageSize: 99999, // Large number to get all records
        client_id: clientID,
        site_id: siteID,
        site_ids: siteIDs,
        sites: siteIDs,
        type,
        search: searchText || '',
        associates: associatesFilter.map((opt) => opt.value),
        leave_types: leaveTypeFilter.map((opt) => opt.value),
        statuses: statusFilter.map((opt) => opt.value),
        year: filterYear,
        month: filterMonth,
      });

      if (response.success) {
        return response.data.list;
      }
      return [];
    } catch (err) {
      console.log(err);
      return [];
    }
  };

  // CSV export helper using your columns config
  const exportToCSV = (data, columns, filename = 'leaves.csv') => {
    if (!data || data.length === 0) {
      alert('No data to export!');
      return;
    }

    // Filter out the "Attachments" column
    const exportableColumns = columns.filter(
      (col) => col.field !== 'attachments',
    );

    // Use headerName for headers
    const headers = exportableColumns.map((col) => col.headerName);
    const csvRows = [];
    csvRows.push(headers.join(',')); // first row

    // Map rows to selected fields
    data.forEach((row) => {
      const values = exportableColumns.map((col) => {
        let val = row[col.field];

        // Special handling for full name (since in renderCell you combine name + last_name)
        if (col.field === 'name') {
          val = `${row.name || ''} ${row.last_name || ''}`.trim();
        }

        if (val === null || val === undefined) val = '';
        return `"${val}"`; // wrap in quotes in case of commas
      });
      csvRows.push(values.join(','));
    });

    // Create blob and download
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    setAssociatesFilter([]);
    setLeaveTypeFilter([]);
    setStatusFilter([]);
  }, [clientID, siteIDsKey]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      // Keep options aligned with the same client, site, date and search scope.
      if (clientID) {
        fetchFilterOptions();
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [
    clientID,
    siteIDsKey,
    filterYear,
    filterMonth,
    searchText,
  ]);

  useEffect(() => {
    fetchTypes();
    fetchAdminClients();
  }, []);

  useEffect(() => {
    const { field, sort } = sortModel[0] || {
      field: 'updatedAt',
      sort: 'desc',
    };
    if (clientID) {
      fetchLeaves(paginationModel, field, sort, searchText);
    }
    fetchClients();
  }, [
    sortModel,
    paginationModel,
    clientID,
    siteID,
    siteIDsKey,
    associatesFilter,
    leaveTypeFilter,
    statusFilter,
    filterYear,
    filterMonth,
    searchText,
  ]);

  useEffect(() => {
    setData(leaves || []);
    setLoading(false);
  }, [leaves]);

  return (
    <>
      <div
        className="container-fluid"
        style={{
          paddingLeft: '2rem',
          paddingRight: '2rem',
          paddingBottom: '6rem',
        }}
      >
        {adminClients?.some((c) => c.client_id === clientID) ? (
          <>
            <div className="border-bottom pb-2 py-2 mb-2 d-flex align-items-center">
              <h6 className="flex-grow-1 pt-4" style={{ color: '#5156BE' }}>
                Leaves | All
              </h6>
            </div>

            <div className="d-flex flex-row align-items-center justify-content-between py-3">
              <h6 className="flex-grow-1">Leaves ({totalRows})</h6>
              <NavLink
                to="/leave/add"
                className="text-decoration-none"
                style={{ color: 'inherit' }}
              >
                <button
                  className="btn btn-outline-brand adjust-fs adjust-shadow"
                  style={{ width: '200px' }}
                >
                  Apply Leave
                </button>
              </NavLink>
            </div>
            <div className="row mb-3 align-items-end">
              {/* Search Bar */}
              <div className="col-lg-2 col-md-4">
                <div className="mb-3">
                  <Searchbar
                    search={searchHandler}
                    placeholder="Search ID or Name"
                    searchValue={searchText}
                  />
                </div>
              </div>

              {false && (
                <>
                  {/* Associates Filter */}
                  <div className="col-lg-2 col-md-4">
                    <div className="mb-3">
                      <label className="form-label font-size-13 text-muted">
                        Associates
                      </label>
                      <Select
                        value={associatesFilter}
                        onChange={setAssociatesFilter}
                        isMulti
                        options={associateOptions}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="Select Associates..."
                        isDisabled={associateOptions.length === 0}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Leave Type Filter */}
              <div className="col-lg-2 col-md-4">
                <div className="mb-3">
                  <label className="form-label font-size-13 text-muted">
                    Leave Type
                  </label>
                  <Select
                    value={leaveTypeFilter}
                    onChange={setLeaveTypeFilter}
                    isMulti
                    options={availableLeaveTypeOptions}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder="Select Leave Types..."
                    isDisabled={availableLeaveTypeOptions.length === 0}
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="col-lg-2 col-md-4">
                <div className="mb-3">
                  <label className="form-label font-size-13 text-muted">
                    Status
                  </label>
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    isMulti
                    options={availableStatusOptions}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder="Select Statuses..."
                    isDisabled={availableStatusOptions.length === 0}
                  />
                </div>
              </div>

              {/* Year Filter */}
              <div className="col-lg-2 col-md-4">
                <div className="mb-3">
                  <label className="form-label font-size-13 text-muted">
                    Year
                  </label>
                  <select
                    className="form-select"
                    value={year ?? ''}
                    onChange={(e) => {
                      const val = e.target.value
                        ? Number(e.target.value)
                        : null;
                      setYear(val);
                      setFilterYear(val);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                  >
                    <option value="">All</option>
                    {Array.from(
                      { length: new Date().getFullYear() - 2025 + 1 },
                      (_, i) => 2025 + i,
                    ).map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Month Filter */}
              <div className="col-lg-2 col-md-4">
                <div className="mb-3">
                  <label className="form-label font-size-13 text-muted">
                    Month
                  </label>
                  <select
                    className="form-select"
                    value={month ?? ''}
                    onChange={(e) => {
                      const val = e.target.value
                        ? Number(e.target.value)
                        : null;
                      setMonth(val);
                      setFilterMonth(val);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                  >
                    <option value="">All</option>
                    {[
                      'January',
                      'February',
                      'March',
                      'April',
                      'May',
                      'June',
                      'July',
                      'August',
                      'September',
                      'October',
                      'November',
                      'December',
                    ].map((m, index) => (
                      <option key={index + 1} value={index + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Export Button */}
              <div className="col-lg-2 col-md-4">
                <div className="mb-3">
                  <label
                    className="form-label font-size-13 text-muted"
                    style={{ visibility: 'hidden' }}
                  >
                    Hidden
                  </label>
                  <Button
                    title={'Export CSV'}
                    loading={loading}
                    onClick={handleExport}
                    assignClassName={
                      'btn btn-outline-secondary w-100 adjust-fs adjust-shadow'
                    }
                  />
                </div>
              </div>
            </div>

            <div className="table-responsive rounded shadow bg-white">
              <Box sx={{ width: '100%', height: 1000 }}>
                {data && (
                  <DataGrid
                    columns={columns}
                    rows={data || []}
                    getRowId={(row) => row.leave_id}
                    rowCount={totalRows || 1}
                    loading={loading}
                    pageSizeOptions={[10, 20, 30, 40, 50, 80, 100]}
                    paginationModel={paginationModel}
                    paginationMode="server"
                    onPaginationModelChange={setPaginationModel}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 10 },
                      },
                      sorting: {
                        sortModel: [{ field: 'updatedAt', sort: 'desc' }],
                      },
                    }}
                    sortingMode="server"
                    style={{ minHeight: 100, fontSize: 14 }}
                    localeText={{ noRowsLabel: '' }}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    getRowHeight={() => 'auto'}
                    sx={{
                      [`& .${gridClasses.cell}`]: { py: 3 },
                      [`& .${gridClasses.columnHeader}`]: {
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#6c757d!important',
                      },
                      [`& .${gridClasses.row}`]: {
                        fontSize: '14px',
                        color: '#5156BE!important',
                        fontWeight: 700,
                      },
                    }}
                    disableRowSelectionOnClick={true}
                  />
                )}
              </Box>
            </div>
          </>
        ) : (
          <div className="text-muted pt-4">
            The admin does not have access to this client.
          </div>
        )}
      </div>
    </>
  );
};

export default withRouter(Leaves);
